import { useEffect, useState } from 'react';
import axios from 'axios';
import { API_BASE } from '../config/api';

// Platform-wide "Programmes-only learner mode" (default ON), fetched once and shared.
let cache = null;
let inflight = null;
let fetchedAt = 0;
const listeners = new Set();
const MAX_AGE_MS = 30000;

const load = () => {
  if (!inflight) {
    inflight = axios.get(`${API_BASE}/api/platform/learner-mode`)
      .then((r) => { cache = !!r.data.programmes_only; })
      .catch(() => { if (cache === null) cache = true; })
      .finally(() => { fetchedAt = Date.now(); inflight = null; listeners.forEach((fn) => fn(cache)); });
  }
  return inflight;
};

// Re-check when the tab regains focus so an admin's change reaches open learner sessions.
if (typeof window !== 'undefined') {
  window.addEventListener('focus', () => { if (Date.now() - fetchedAt > MAX_AGE_MS) load(); });
}

export const setLearnerModeCache = (value) => {
  cache = value;
  listeners.forEach((fn) => fn(cache));
};

export function useLearnerMode() {
  const [value, setValue] = useState(cache);
  useEffect(() => {
    listeners.add(setValue);
    if (cache === null || Date.now() - fetchedAt > MAX_AGE_MS) load();
    if (cache !== null) setValue(cache);
    return () => listeners.delete(setValue);
  }, []);
  return { programmesOnly: value === null ? true : value, loaded: value !== null };
}

// Learner-facing paths allowed in programmes-only mode (players are reached from programme activities)
const ALLOWED = [
  /^\/dashboard\/?$/,
  /^\/profile\/?$/,
  /^\/settings\/?$/,
  /^\/programmes(\/[^/]+)?\/?$/,
  /^\/programmes\/[^/]+\/assignments\/[^/]+\/?$/,
  /^\/courses\/[^/]+\/(learn|ai-learn)\/?$/,
  /^\/labs\/[^/]+\/?$/,
  /^\/labs-v2\/[^/]+\/?$/,
  /^\/masterclasses\/[^/]+\/?$/,
  /^\/scorm\/[^/]+\/?$/,
  /^\/login\/?$/,
  /^\/sso\/callback/,
  /^\/auth\/callback/,
  /^\/lti\/launch/,
  /^\/(reset-password|forgot-password|verify-email|accept-invite)/,
];

// Instructor Hub pages are only reachable by users with the instructor role
const INSTRUCTOR_PATH = /^\/instructor(\/programmes\/[^/]+(\/gradebook)?)?\/?$/;

export const isLearnerPathAllowed = (path, role) => ALLOWED.some((re) => re.test(path)) || (role === 'instructor' && INSTRUCTOR_PATH.test(path));
